import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EvaOsMark } from "@/components/landing/EvaOsMark";
import { openCallMeNow } from "@/lib/openCallMeNow";
import { PALETTE, FONT_BODY, FONT_MONO, PRODUCTS, ProductKey } from "./tokens";

export interface NavSection { href: string; label: string }

export interface SiteNavProps {
  /** Which product surface we're on — drives the active state in the product row. */
  product: ProductKey;
  /** Optional in-page anchors. Rendered as a slim secondary row once scrolled, and in the mobile sheet. */
  sections?: NavSection[];
  /** Currently visible section id (no leading #). */
  activeSection?: string | null;
  /** Page-specific primary CTA. Omit for a call-only nav. */
  primary?: { label: string; href?: string; to?: string };
  /** Lead source prefix for the "Talk to Us Now" button. */
  callSource: string;
  /** Show the Eva → CoBrain upgrade hover card on the CoBrain product link. */
  cobrainHint?: boolean;
}

const PhoneIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export function SiteNav({ product, sections = [], activeSection, primary, callSource, cobrainHint }: SiteNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    h();
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [menuOpen]);

  // The secondary in-page row only appears once the nav has a background, so it
  // never pushes into a page hero.
  const showSections = sections.length > 0 && scrolled;

  const primaryPill: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "9px 18px", background: PALETTE.ink, color: "#fff",
    borderRadius: 999, textDecoration: "none", fontSize: 13, fontWeight: 500,
  };

  return (
    <nav
      data-site-nav
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        padding: "18px 24px",
        background: scrolled ? "rgba(248,244,236,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(12px) saturate(140%)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(12px) saturate(140%)" : "none",
        borderBottom: scrolled ? `1px solid ${PALETTE.line}` : "1px solid transparent",
        transition: "background 200ms, border-color 200ms, backdrop-filter 200ms",
        fontFamily: FONT_BODY,
      }}
    >
      <div className="site-nav-row" style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 24 }}>
        <Link to="/" aria-label="HireEva home" style={{ display: "inline-flex", alignItems: "center", justifySelf: "start", textDecoration: "none" }}>
          <EvaOsMark color={PALETTE.ink} accent={PALETTE.amber} />
        </Link>

        {/* Product row — identical on every page */}
        <div className="site-nav-products" style={{ display: "flex", alignItems: "center", gap: 28, justifySelf: "center" }}>
          {PRODUCTS.map((p) => {
            const isActive = p.key === product;
            const withHint = cobrainHint && p.key === "cobrain" && product !== "cobrain";
            const link = (
              <Link
                key={p.key}
                to={p.to}
                aria-current={isActive ? "page" : undefined}
                style={{
                  position: "relative", fontSize: 14,
                  color: isActive ? PALETTE.ink : PALETTE.ink2,
                  textDecoration: "none", fontWeight: isActive ? 600 : 400,
                  paddingBottom: 4, transition: "color 200ms",
                }}
              >
                {p.label}
                <span aria-hidden style={{
                  position: "absolute", left: 0, right: 0, bottom: -2, height: 2,
                  background: PALETTE.amber, borderRadius: 2,
                  transform: isActive ? "scaleX(1)" : "scaleX(0)",
                  transformOrigin: "left",
                  transition: "transform 280ms cubic-bezier(.2,.8,.2,1)",
                }} />
              </Link>
            );
            if (!withHint) return link;
            return (
              <span key={p.key} className="site-nav-hint-wrap" style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                {link}
                <span aria-hidden className="site-nav-hint" style={{
                  position: "absolute", top: "calc(100% + 14px)", left: "50%",
                  minWidth: 280, padding: "14px 16px", borderRadius: 14,
                  background: "rgba(255,255,255,0.98)",
                  border: `1px solid ${PALETTE.line}`,
                  boxShadow: "0 16px 40px -12px rgba(0,0,0,0.18)",
                  fontSize: 13, lineHeight: 1.45, color: PALETTE.ink2, zIndex: 60,
                }}>
                  <span style={{
                    position: "absolute", top: -6, left: "50%", transform: "translateX(-50%) rotate(45deg)",
                    width: 12, height: 12, background: "rgba(255,255,255,0.98)",
                    borderTop: `1px solid ${PALETTE.line}`, borderLeft: `1px solid ${PALETTE.line}`,
                  }} />
                  <span style={{
                    display: "block", fontFamily: FONT_MONO, fontSize: 10,
                    letterSpacing: "0.14em", textTransform: "uppercase",
                    color: PALETTE.amber, fontWeight: 700, marginBottom: 6,
                  }}>The upgrade path</span>
                  <span style={{ display: "block", color: PALETTE.ink, fontWeight: 600, marginBottom: 4 }}>Eva → CoBrain</span>
                  <span style={{ display: "block" }}>Outgrow the owner's seat? Plug your whole team into one shared brain.</span>
                </span>
              </span>
            );
          })}
        </div>

        {/* Right: call + optional page CTA */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, justifySelf: "end" }}>
          {primary && (
            <button
              type="button"
              onClick={() => openCallMeNow(`${callSource}_desktop`)}
              className="site-nav-call"
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "9px 16px", border: `1px solid ${PALETTE.ink}`, borderRadius: 999,
                background: "transparent", cursor: "pointer", fontFamily: "inherit",
                color: PALETTE.ink, fontSize: 13, fontWeight: 500,
              }}
            >
              <PhoneIcon /> Talk to Us Now
            </button>
          )}
          {primary ? (primary.to
            ? <Link to={primary.to} style={primaryPill} data-nav-hire-pill>{primary.label}</Link>
            : <a href={primary.href} style={primaryPill} data-nav-hire-pill>{primary.label}</a>
          ) : (
            <button
              type="button"
              onClick={() => openCallMeNow(`${callSource}_primary`)}
              className="site-nav-primary-call"
              aria-label="Talk to us now"
              style={{ ...primaryPill, border: 0, cursor: "pointer", fontFamily: "inherit" }}
            >
              <PhoneIcon /> <span className="site-nav-primary-label">Talk to Us Now</span>
            </button>
          )}
          <button
            type="button"
            className="site-nav-hamburger"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              display: "none", background: "transparent", border: 0, padding: 8, marginLeft: 2,
              cursor: "pointer", color: PALETTE.ink, lineHeight: 0,
            }}
          >
            {menuOpen
              ? <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
              : <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></svg>}
          </button>
        </div>
      </div>

      {/* Secondary in-page row (desktop, scrolled only) */}
      {sections.length > 0 && (
        <div
          className="site-nav-sections"
          aria-hidden={!showSections}
          style={{
            maxWidth: 1200, margin: "0 auto",
            display: "flex", justifyContent: "center", flexWrap: "wrap",
            gap: 24, overflow: "hidden",
            maxHeight: showSections ? 34 : 0,
            opacity: showSections ? 1 : 0,
            paddingTop: showSections ? 10 : 0,
            transition: "max-height 260ms ease, opacity 200ms ease, padding-top 260ms ease",
          }}
        >
          {sections.map((s) => {
            const isActive = !!activeSection && `#${activeSection}` === s.href;
            return (
              <a
                key={s.href}
                href={s.href}
                tabIndex={showSections ? 0 : -1}
                style={{
                  fontSize: 12.5,
                  color: isActive ? PALETTE.ink : PALETTE.muted,
                  fontWeight: isActive ? 600 : 400,
                  textDecoration: "none",
                }}
              >
                {s.label}
              </a>
            );
          })}
        </div>
      )}

      {/* Mobile overlay + sheet */}
      <div
        aria-hidden={!menuOpen}
        onClick={() => setMenuOpen(false)}
        style={{
          position: "fixed", inset: 0, top: 0, zIndex: 49,
          background: "rgba(0,0,0,0.18)",
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? "auto" : "none",
          transition: "opacity 200ms ease",
        }}
      />
      <div
        className="site-nav-sheet"
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed", left: 0, right: 0, top: 64, zIndex: 50,
          background: PALETTE.bg, borderBottom: `1px solid ${PALETTE.line}`,
          padding: "18px 24px 24px",
          maxHeight: "calc(100vh - 64px)", overflowY: "auto",
          transform: menuOpen ? "translateY(0)" : "translateY(-12px)",
          opacity: menuOpen ? 1 : 0,
          visibility: menuOpen ? "visible" : "hidden",
          pointerEvents: menuOpen ? "auto" : "none",
          transition: `opacity 220ms ease, transform 260ms cubic-bezier(.2,.8,.2,1), visibility 0s linear ${menuOpen ? 0 : 220}ms`,
          display: "flex", flexDirection: "column", gap: 4,
        }}
      >
        <span style={{
          fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.16em",
          textTransform: "uppercase", color: PALETTE.muted, padding: "0 4px 8px",
        }}>Products</span>
        {PRODUCTS.map((p) => {
          const isActive = p.key === product;
          return (
            <Link
              key={p.key}
              to={p.to}
              onClick={() => setMenuOpen(false)}
              aria-current={isActive ? "page" : undefined}
              style={{
                padding: "14px 4px", fontSize: 17, textDecoration: "none",
                color: PALETTE.ink, fontWeight: isActive ? 600 : 400,
                borderBottom: `1px solid ${PALETTE.line}`,
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {p.label}
              {isActive && (
                <span style={{
                  marginLeft: "auto", fontSize: 11, color: PALETTE.amber,
                  fontFamily: FONT_MONO, letterSpacing: "0.12em", textTransform: "uppercase",
                }}>current</span>
              )}
            </Link>
          );
        })}
        {sections.length > 0 && (
          <>
            <span style={{
              fontFamily: FONT_MONO, fontSize: 10, letterSpacing: "0.16em",
              textTransform: "uppercase", color: PALETTE.muted, padding: "16px 4px 8px",
            }}>On this page</span>
            {sections.map((s) => (
              <a
                key={s.href}
                href={s.href}
                onClick={() => setMenuOpen(false)}
                style={{
                  padding: "12px 4px", fontSize: 15.5, color: PALETTE.ink2, textDecoration: "none",
                  borderBottom: `1px solid ${PALETTE.line}`,
                }}
              >{s.label}</a>
            ))}
          </>
        )}
        <button
          type="button"
          onClick={() => { setMenuOpen(false); openCallMeNow(`${callSource}_mobile`); }}
          style={{
            marginTop: 16, padding: "14px 4px", fontSize: 17, color: PALETTE.ink,
            background: "transparent", border: 0, cursor: "pointer",
            fontFamily: "inherit", textAlign: "left", width: "100%",
            borderBottom: `1px solid ${PALETTE.line}`,
          }}
        >Talk to Us Now</button>
        {primary && (primary.to
          ? <Link to={primary.to} onClick={() => setMenuOpen(false)} style={{ ...primaryPill, marginTop: 18, justifyContent: "center", padding: "14px 22px", fontSize: 14 }}>{primary.label}</Link>
          : <a href={primary.href} onClick={() => setMenuOpen(false)} style={{ ...primaryPill, marginTop: 18, justifyContent: "center", padding: "14px 22px", fontSize: 14 }}>{primary.label}</a>
        )}
      </div>

      <style>{`
        .site-nav-hint { opacity: 0; visibility: hidden; transform: translate(-50%, 4px); transition: opacity 200ms ease, transform 200ms ease, visibility 200ms; }
        .site-nav-hint-wrap:hover .site-nav-hint, .site-nav-hint-wrap:focus-within .site-nav-hint { opacity: 1; visibility: visible; transform: translate(-50%, 0); }
        @media (max-width: 900px) {
          .site-nav-hint { display: none; }
          .site-nav-products { display: none !important; }
          .site-nav-sections { display: none !important; }
          .site-nav-call { display: none !important; }
          .site-nav-hamburger { display: inline-flex !important; }
          .site-nav-row { grid-template-columns: 1fr auto !important; }
        }
        @media (max-width: 600px) {
          /* Compact icon-only call control so the nav never doubles up with a
             hero CTA or the sticky mobile pill. */
          .site-nav-primary-label { display: none; }
          .site-nav-primary-call { padding: 0 !important; width: 42px; height: 42px; justify-content: center; }
          [data-nav-hire-pill] { padding: 9px 14px !important; font-size: 12.5px !important; }
        }
        @media print { [data-site-nav] { display: none !important; } }
      `}</style>
    </nav>
  );
}

/** Spacer for pages whose content starts immediately under the fixed nav. */
export const SiteNavSpacer = () => <div aria-hidden style={{ height: 68 }} />;

export default SiteNav;